import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef } from 'react';
import { stepType } from 'utils/reactFlowUtils';

const svgNS = 'http://www.w3.org/2000/svg';
const stalledSymbol = {
  primaryCircleRadius: 9,
  radius: 11,
};
const collapsedBranch = {
  height: 44,
  nodeGap: 10,
  pathGap: 15,
  pathOffsetLeft: 25,
  strokeWidth: 2,
  backgroundOffset: 1,
  circleRadius: 4.5,
  cornerRadius: 10,
};
const expandedBranch = {
  height: 70,
  nodeGap: 45,
  pathGap: 20,
  pathOffsetLeft: 28,
  strokeWidth: 2,
  backgroundOffset: 1,
  circleRadius: 4.5,
  cornerRadius: 10,
};

function drawBranch(
  branch,
  parentNodeOffsetLeft,
  childNodeOffsetLeft,
  index,
) {
  // 1. Get the center [x, y] of parent and child circles
  const parentCircle = [
    parentNodeOffsetLeft + branch.pathOffsetLeft
    + Math.max(index, 0) * branch.pathGap,
    0,
  ];
  const childCircle = [
    childNodeOffsetLeft + branch.pathOffsetLeft,
    branch.height,
  ];

  // 2. Calculate delta x
  const dx = childCircle[0] - parentCircle[0];
  if (dx === 0) {
    // a simple straight line
    return {
      branchPath: `
        M ${parentCircle[0]} ${parentCircle[1]}
        V ${branch.height}
      `,
      parentCircle,
      childCircle,
    };
  }
  const toRight = dx > 0;

  // 3. Draw branch path with corner radius
  const actualCornerRadius = Math.min(
    branch.cornerRadius,
    Math.abs(dx) / 2,
  );
  const branchPath = `
    M ${parentCircle[0]} ${parentCircle[1]}
    v ${branch.height / 2 - actualCornerRadius}
    a ${actualCornerRadius} ${actualCornerRadius} 0 0 ${toRight ? '0' : '1'} ${toRight ? actualCornerRadius : -1 * actualCornerRadius} ${actualCornerRadius}
    ${Math.abs(dx) > actualCornerRadius * 2 ? `h ${dx - actualCornerRadius * (toRight ? 2 : -2)}` : ''}
    a ${actualCornerRadius} ${actualCornerRadius} 0 0 ${toRight ? '1' : '0'} ${toRight ? actualCornerRadius : -1 * actualCornerRadius} ${actualCornerRadius}
    V ${branch.height}
  `;
  return {
    branchPath,
    parentCircle,
    childCircle,
  };
}

function drawConnectionPoint(branch, cx, cy) {
  const svgGroup = document.createElementNS(svgNS, 'g');
  const svgConnectionPoint = document.createElementNS(svgNS, 'circle');
  svgConnectionPoint.setAttribute('cx', cx);
  svgConnectionPoint.setAttribute('cy', cy);
  const svgConnectionPointBackground = svgConnectionPoint.cloneNode();
  svgConnectionPointBackground.setAttribute('r', branch.circleRadius + 2);
  svgConnectionPointBackground.setAttribute('class', 'background');
  svgGroup.appendChild(svgConnectionPointBackground);
  svgConnectionPoint.setAttribute('r', branch.circleRadius);
  svgConnectionPoint.setAttribute('class', 'connectionPoint');
  svgGroup.appendChild(svgConnectionPoint);

  return svgGroup;
}

function drawStalledSymbol(cx, cy) {
  const lineOffset = stalledSymbol.primaryCircleRadius * Math.sin(0.785398);

  const svgGroup = document.createElementNS(svgNS, 'g');
  svgGroup.setAttribute('class', 'stalledSymbol');

  const svgBaseCircle = document.createElementNS(svgNS, 'circle');
  svgBaseCircle.setAttribute('cx', cx);
  svgBaseCircle.setAttribute('cy', cy);

  const svgBackgroundCircle = svgBaseCircle.cloneNode();
  svgBackgroundCircle.setAttribute('class', 'background');
  svgBackgroundCircle.setAttribute('r', stalledSymbol.radius);
  svgGroup.appendChild(svgBackgroundCircle);

  const svgPrimaryCircle = svgBaseCircle.cloneNode();
  svgPrimaryCircle.setAttribute('class', 'primary');
  svgPrimaryCircle.setAttribute('r', stalledSymbol.primaryCircleRadius);
  svgGroup.appendChild(svgPrimaryCircle);

  const svgLine = document.createElementNS(svgNS, 'line');
  svgLine.setAttribute('x1', cx + lineOffset);
  svgLine.setAttribute('y1', cy - lineOffset);
  svgLine.setAttribute('x2', cx - lineOffset);
  svgLine.setAttribute('y2', cy + lineOffset);
  svgGroup.appendChild(svgLine);

  return svgGroup;
}

export default function Branches({
  type,
  level,
  levelStatus = undefined,
  nodeData,
  updatePaths,
}) {
  const branch = useMemo(() => {
    if (type === 'collapsed') return { ...collapsedBranch };
    return { ...expandedBranch };
  }, [type]);
  const trueHeight = useMemo(
    () => branch.height
      + branch.circleRadius
      + branch.backgroundOffset
      + branch.strokeWidth
      + stalledSymbol.radius,
    [
      branch.height,
      branch.circleRadius,
      branch.backgroundOffset,
      branch.strokeWidth,
    ],
  );
  const svgRef = useRef();

  useEffect(() => {
    const svg = svgRef.current;
    let totalWidth = 0;
    let minWidth = 0; // width required to reach parent connection point
    if (!svg) return;

    while (svg.firstChild) {
      // remove all child elements from <svg>
      svg.removeChild(svg.firstChild);
    }

    level.forEach((node) => {
      // 2. Get node position and width
      const nodeDatum = nodeData.current[node.uniqueId];
      if (!nodeDatum) return;
      const nodeOffsetLeft = nodeDatum.offsetLeft;
      totalWidth += totalWidth === 0
        ? nodeDatum.width
        : nodeDatum.width + branch.nodeGap;

      // 3. Get parent node position
      const parentNodeDatum = nodeData.current[node.parentStep];
      if (!parentNodeDatum) return;
      const parentNodeOffsetLeft = parentNodeDatum.offsetLeft;
      minWidth = Math.max(
        minWidth,
        parentNodeOffsetLeft + branch.pathOffsetLeft + stalledSymbol.radius + 1,
      );

      // 4. Generate path and circles
      const { branchPath, parentCircle, childCircle } = drawBranch(
        branch,
        parentNodeOffsetLeft,
        nodeOffsetLeft,
        parentNodeDatum.childSteps.indexOf(node.uniqueId),
      );

      // 5. Update DOM
      const svgGroup = document.createElementNS(svgNS, 'g');
      svgGroup.setAttribute('class', levelStatus?.[node.uniqueId]);
      // path
      const svgPath = document.createElementNS(svgNS, 'path');
      svgPath.setAttribute('d', branchPath);
      const svgPathBackground = svgPath.cloneNode();
      svgPathBackground.setAttribute('class', 'pathBackground');
      svgGroup.appendChild(svgPathBackground);
      svgPath.setAttribute('class', 'path');
      svgGroup.appendChild(svgPath);
      // parent circle
      svgGroup.appendChild(
        drawConnectionPoint(branch, ...parentCircle),
      );
      // child circle
      if (levelStatus?.[node.uniqueId] === 'stalled') {
        svgGroup.appendChild(
          drawStalledSymbol(...childCircle),
        );
      } else {
        svgGroup.appendChild(
          drawConnectionPoint(branch, ...childCircle),
        );
      }
      svg.appendChild(svgGroup);
    });

    // 6. Update <svg> viewBox
    svg.setAttribute(
      'viewBox',
      `
        0
        ${-1 * branch.circleRadius - branch.backgroundOffset}
        ${Math.max(totalWidth, minWidth)}
        ${trueHeight}`,
    );
  }, [updatePaths, branch, level, levelStatus, nodeData, trueHeight]);

  return (
    <svg
      ref={svgRef}
      className="branches collapsed"
      height={`${trueHeight}px`}
      style={{
        marginTop: `${-1 * branch.circleRadius - branch.backgroundOffset}px`,
        marginBottom: `${-1 * stalledSymbol.radius - 2}px`,
        strokeWidth: `${branch.strokeWidth}px`,
      }}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`
        0
        ${-1 * branch.circleRadius - branch.backgroundOffset}
        0
        ${trueHeight}`}
    />
  );
}

Branches.propTypes = {
  type: PropTypes.oneOf(
    ['collapsed', 'expanded'],
  ).isRequired,
  level: PropTypes.arrayOf(stepType).isRequired,
  levelStatus: PropTypes.arrayOf(
    PropTypes.objectOf(PropTypes.string),
  ),
  nodeData: PropTypes.objectOf(
    PropTypes.shape({
      width: PropTypes.number,
      offsetLeft: PropTypes.number,
      childSteps: PropTypes.arrayOf(PropTypes.string),
    }),
  ).isRequired,
  updatePaths: PropTypes.number.isRequired,
};
